
import React, { Component } from "react";
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    Divider,
    InputAdornment,
} from "@mui/material";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";

import { Formik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import axios from "axios";

import config from "../../config.json";

class RegisterPage extends Component {
    apiEndPoint = config.apiEndPoint + "users/register";
    recaptchaRef = React.createRef();

    schema = Yup.object().shape({
        username: Yup.string()
            .required("Username is required")
            .min(4)
            .max(15),

        password: Yup.string()
            .required("Password is required")
            .min(4)
            .max(15),

        confirmPassword: Yup.string().oneOf(
            [Yup.ref("password"), null],
            "Passwords must match"
        ),

        email: Yup.string()
            .required("Email is required")
            .email("Enter a valid email"),
    });

    async doSubmit(values, captchaResponse) {
        try {
            await axios.post(this.apiEndPoint, {
                ...values,
                captchaResponse,
            });

            toast.success("Account created successfully!");

            setTimeout(() => {
                window.location = "/downloads";
            }, 2500);
        } catch (ex) {
            toast.error(
                ex?.response?.data || "Registration failed"
            );
            this.recaptchaRef.current.reset();
        }
    }

    render() {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: { xs: 5, md: 8 },
                    px: { xs: 2, sm: 3 },

                    background:
                        "radial-gradient(circle at 50% -10%, rgba(255,70,25,0.16), transparent 34%), radial-gradient(circle at 0% 100%, rgba(190,35,10,0.10), transparent 32%), radial-gradient(circle at 100% 50%, rgba(255,95,30,0.055), transparent 30%), linear-gradient(180deg, #030303 0%, #080707 50%, #020202 100%)",

                    color: "#fff",

                    "&:before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        opacity: 0.18,
                        backgroundImage:
                            "linear-gradient(rgba(255,75,30,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,75,30,0.08) 1px, transparent 1px)",
                        backgroundSize: "45px 45px",
                    },

                    "&:after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "65%",
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,72,28,0.9), rgba(255,145,65,0.7), rgba(255,72,28,0.9), transparent)",
                        boxShadow:
                            "0 0 25px rgba(255,65,20,0.5)",
                    },
                }}
            >
                {/* =====================================================
                    TOP LEFT FIRE GLOW
                ====================================================== */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "-260px",
                        left: "-260px",
                        width: 650,
                        height: 650,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(255,62,20,0.18) 0%, rgba(190,30,8,0.06) 35%, transparent 70%)",
                        filter: "blur(80px)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    BOTTOM RIGHT FIRE GLOW
                ====================================================== */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-300px",
                        right: "-250px",
                        width: 700,
                        height: 700,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(255,80,25,0.13) 0%, rgba(160,25,8,0.04) 42%, transparent 72%)",
                        filter: "blur(95px)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    CENTER RED GLOW
                ====================================================== */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "43%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 560,
                        height: 560,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(255,70,25,0.045), transparent 70%)",
                        filter: "blur(100px)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    DECORATIVE FIRE LINES
                ====================================================== */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "18%",
                        left: 0,
                        width: { xs: "22%", md: "32%" },
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,68,25,0.35), transparent)",
                        pointerEvents: "none",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        bottom: "18%",
                        right: 0,
                        width: { xs: "22%", md: "32%" },
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,68,25,0.35), transparent)",
                        pointerEvents: "none",
                    }}
                />

                <Container
                    maxWidth="md"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    {/* =================================================
                        TOP BRANDING
                    ================================================== */}
                    <Box
                        sx={{
                            textAlign: "center",
                            mb: 3,
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#ff6a32",
                                fontWeight: 900,
                                letterSpacing: "5px",
                                fontSize: {
                                    xs: "9px",
                                    sm: "10px",
                                },
                                textTransform: "uppercase",
                                mb: 1,
                                textShadow:
                                    "0 0 18px rgba(255,75,30,0.35)",
                            }}
                        >
                            VEX CONQUER • CLASSIC 5165
                        </Typography>

                        <Typography
                            sx={{
                                color: "#fff",
                                fontWeight: 900,
                                fontSize: {
                                    xs: "20px",
                                    sm: "25px",
                                    md: "28px",
                                },
                                letterSpacing: "-0.5px",
                                lineHeight: 1.1,
                            }}
                        >
                            YOUR BATTLE BEGINS HERE
                        </Typography>

                        <Typography
                            sx={{
                                color:
                                    "rgba(255,255,255,0.38)",
                                fontSize: "11px",
                                mt: 1,
                            }}
                        >
                            Create your warrior and enter the
                            classic world.
                        </Typography>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: {
                                xs: "20px",
                                sm: "26px",
                            },

                            background:
                                "linear-gradient(145deg, rgba(17,12,10,0.99), rgba(5,5,5,0.99))",

                            border:
                                "1px solid rgba(255,76,30,0.20)",

                            boxShadow:
                                "0 35px 100px rgba(0,0,0,0.75), 0 0 70px rgba(160,35,10,0.07), inset 0 1px 0 rgba(255,255,255,0.025)",

                            "&:before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "2px",
                                background:
                                    "linear-gradient(90deg, transparent, #d92d0c, #ff7135, #ff9a4d, #d92d0c, transparent)",
                                boxShadow:
                                    "0 0 18px rgba(255,65,20,0.5)",
                            },

                            "&:after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: 180,
                                height: 180,
                                background:
                                    "radial-gradient(circle, rgba(255,65,20,0.07), transparent 70%)",
                                pointerEvents: "none",
                            },
                        }}
                    >
                        {/* =================================================
                            HEADER
                        ================================================== */}
                        <Box
                            sx={{
                                position: "relative",
                                textAlign: "center",
                                py: {
                                    xs: 3.5,
                                    md: 4.5,
                                },
                                px: 3,
                                overflow: "hidden",

                                borderBottom:
                                    "1px solid rgba(255,76,30,0.10)",

                                background:
                                    "linear-gradient(145deg, rgba(255,65,20,0.055), rgba(8,8,8,0.85))",
                            }}
                        >
                            {/* HEADER GLOW */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "-150px",
                                    left: "50%",
                                    transform:
                                        "translateX(-50%)",
                                    width: 420,
                                    height: 320,
                                    borderRadius: "50%",
                                    background:
                                        "radial-gradient(circle, rgba(255,67,25,0.17), transparent 70%)",
                                    filter: "blur(70px)",
                                    pointerEvents: "none",
                                }}
                            />

                            {/* ICON */}
                            <Box
                                sx={{
                                    position: "relative",
                                    width: {
                                        xs: 66,
                                        md: 76,
                                    },
                                    height: {
                                        xs: 66,
                                        md: 76,
                                    },
                                    mx: "auto",
                                    mb: 2.2,
                                    borderRadius: "20px",

                                    background:
                                        "linear-gradient(145deg, #ff7135 0%, #d52f0c 48%, #821c0b 100%)",

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",

                                    border:
                                        "1px solid rgba(255,160,100,0.25)",

                                    boxShadow:
                                        "0 15px 45px rgba(220,50,15,0.25), 0 0 35px rgba(255,65,20,0.13), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                <HowToRegRoundedIcon
                                    sx={{
                                        fontSize: {
                                            xs: 34,
                                            md: 40,
                                        },
                                        color: "#fff",
                                        filter:
                                            "drop-shadow(0 2px 5px rgba(0,0,0,0.4))",
                                    }}
                                />
                            </Box>

                            {/* SMALL LABEL */}
                            <Typography
                                sx={{
                                    position: "relative",
                                    color: "#ff7135",
                                    fontWeight: 900,
                                    letterSpacing: "4px",
                                    fontSize: "10px",
                                    mb: 1,
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                JOIN THE BATTLE
                            </Typography>

                            {/* TITLE */}
                            <Typography
                                sx={{
                                    position: "relative",
                                    color: "#fff",
                                    fontWeight: 900,
                                    fontSize: {
                                        xs: "28px",
                                        sm: "34px",
                                        md: "40px",
                                    },
                                    lineHeight: 1,
                                    letterSpacing: "-0.7px",
                                }}
                            >
                                CREATE ACCOUNT
                            </Typography>

                            <Typography
                                sx={{
                                    position: "relative",
                                    color:
                                        "rgba(255,255,255,0.45)",
                                    mt: 1.5,
                                    fontSize: {
                                        xs: "12px",
                                        md: "13px",
                                    },
                                }}
                            >
                                Create your warrior and begin
                                your Vex Conquer journey.
                            </Typography>

                            {/* SERVER TAGS */}
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mt: 2.5,
                                }}
                            >
                                {[
                                    "5165 CLASSIC",
                                    "LEVEL 130",
                                    "1ST REBORN",
                                    "CLASSIC PVP",
                                ].map((item) => (
                                    <Box
                                        key={item}
                                        sx={{
                                            px: 1.5,
                                            py: 0.65,
                                            borderRadius:
                                                "6px",
                                            background:
                                                "rgba(255,65,20,0.055)",
                                            border:
                                                "1px solid rgba(255,75,30,0.13)",
                                            color:
                                                "rgba(255,255,255,0.55)",
                                            fontSize: "8px",
                                            fontWeight: 800,
                                            letterSpacing:
                                                "1px",
                                        }}
                                    >
                                        {item}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* =================================================
                            FORM
                        ================================================== */}
                        <Box
                            sx={{
                                p: {
                                    xs: 3,
                                    sm: 4,
                                    md: 5,
                                },
                            }}
                        >
                            <Formik
                                validationSchema={this.schema}
                                initialValues={{
                                    username: "",
                                    password: "",
                                    confirmPassword: "",
                                    email: "",
                                }}
                                onSubmit={(values) =>
                                    this.doSubmit(
                                        values,
                                        this.recaptchaRef.current.getValue()
                                    )
                                }
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    handleSubmit,
                                }) => (
                                    <form onSubmit={handleSubmit}>

                                        {/* FORM LABEL */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems:
                                                    "center",
                                                gap: 1.5,
                                                mb: 2.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 4,
                                                    height: 24,
                                                    borderRadius:
                                                        "10px",
                                                    background:
                                                        "linear-gradient(180deg, #ff7135, #b5220b)",
                                                    boxShadow:
                                                        "0 0 14px rgba(255,65,20,0.45)",
                                                }}
                                            />

                                            <Box>
                                                <Typography
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 900,
                                                        fontSize: "14px",
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    ACCOUNT
                                                    INFORMATION
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color:
                                                            "rgba(255,255,255,0.30)",
                                                        fontSize: "9px",
                                                        mt: 0.4,
                                                        letterSpacing:
                                                            "0.5px",
                                                    }}
                                                >
                                                    CREATE YOUR
                                                    WARRIOR PROFILE
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* USERNAME */}
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            name="username"
                                            value={
                                                values.username
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            onBlur={handleBlur}
                                            sx={fieldStyle}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonOutlineIcon
                                                            sx={{
                                                                color: "#ff7135",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.username &&
                                            touched.username && (
                                                <ErrorText>
                                                    {
                                                        errors.username
                                                    }
                                                </ErrorText>
                                            )}

                                        {/* EMAIL */}
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={
                                                values.email
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            onBlur={handleBlur}
                                            sx={{
                                                ...fieldStyle,
                                                mt: 2.5,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailOutlinedIcon
                                                            sx={{
                                                                color: "#ff7135",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.email &&
                                            touched.email && (
                                                <ErrorText>
                                                    {
                                                        errors.email
                                                    }
                                                </ErrorText>
                                            )}

                                        {/* PASSWORD */}
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Password"
                                            name="password"
                                            value={
                                                values.password
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            onBlur={handleBlur}
                                            sx={{
                                                ...fieldStyle,
                                                mt: 2.5,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                color: "#ff7135",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.password &&
                                            touched.password && (
                                                <ErrorText>
                                                    {
                                                        errors.password
                                                    }
                                                </ErrorText>
                                            )}

                                        {/* CONFIRM PASSWORD */}
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Confirm Password"
                                            name="confirmPassword"
                                            value={
                                                values.confirmPassword
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            onBlur={handleBlur}
                                            sx={{
                                                ...fieldStyle,
                                                mt: 2.5,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                color: "#ff7135",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.confirmPassword &&
                                            touched.confirmPassword && (
                                                <ErrorText>
                                                    {
                                                        errors.confirmPassword
                                                    }
                                                </ErrorText>
                                            )}

                                        {/* =================================================
                                            ACCOUNT REQUIREMENTS
                                        ================================================== */}
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "repeat(2, 1fr)",
                                                gap: 1,
                                                mt: 2.5,
                                            }}
                                        >
                                            <Requirement
                                                text="4–15 CHARACTER USERNAME"
                                            />

                                            <Requirement
                                                text="4–15 CHARACTER PASSWORD"
                                            />

                                            <Requirement
                                                text="VALID EMAIL ADDRESS"
                                            />

                                            <Requirement
                                                text="CONFIRM PASSWORD"
                                            />
                                        </Box>

                                        {/* DIVIDER */}
                                        <Divider
                                            sx={{
                                                my: 3.5,
                                                borderColor:
                                                    "rgba(255,255,255,0.06)",
                                            }}
                                        />

                                        {/* CAPTCHA */}
                                        <Typography
                                            sx={{
                                                color:
                                                    "rgba(255,255,255,0.45)",
                                                fontSize: "10px",
                                                fontWeight: 800,
                                                letterSpacing:
                                                    "1.5px",
                                                textAlign: "center",
                                                mb: 1.2,
                                            }}
                                        >
                                            HUMAN VERIFICATION
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                                minHeight: 78,
                                                p: 1.5,
                                                borderRadius:
                                                    "14px",
                                                background:
                                                    "linear-gradient(145deg, rgba(255,255,255,0.025), rgba(255,65,20,0.015))",
                                                border:
                                                    "1px solid rgba(255,75,30,0.09)",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <ReCAPTCHA
                                                ref={
                                                    this
                                                        .recaptchaRef
                                                }
                                                sitekey={
                                                    config.RECAPTCHA_SITE_KEY
                                                }
                                            />
                                        </Box>

                                        {/* CREATE ACCOUNT BUTTON */}
                                        <Button
                                            type="submit"
                                            fullWidth
                                            sx={{
                                                mt: 3,
                                                py: 1.75,
                                                borderRadius:
                                                    "13px",

                                                background:
                                                    "linear-gradient(135deg, #ff7135 0%, #e13b13 48%, #9f210c 100%)",

                                                color: "#fff",
                                                fontWeight: 900,
                                                fontSize: "14px",
                                                letterSpacing:
                                                    "1px",
                                                textTransform:
                                                    "none",

                                                border:
                                                    "1px solid rgba(255,145,95,0.20)",

                                                boxShadow:
                                                    "0 12px 35px rgba(210,45,12,0.22), 0 0 25px rgba(255,65,20,0.08), inset 0 1px 0 rgba(255,255,255,0.20)",

                                                transition:
                                                    "all 0.25s ease",

                                                "&:hover": {
                                                    background:
                                                        "linear-gradient(135deg, #ff8a55 0%, #f04b1b 48%, #b72a10 100%)",

                                                    transform:
                                                        "translateY(-2px)",

                                                    boxShadow:
                                                        "0 17px 42px rgba(220,50,15,0.32), 0 0 30px rgba(255,65,20,0.12)",
                                                },

                                                "&:active": {
                                                    transform:
                                                        "translateY(0)",
                                                },
                                            }}
                                        >
                                            ⚔ CREATE MY ACCOUNT
                                        </Button>

                                        {/* LOGIN */}
                                        <Typography
                                            sx={{
                                                textAlign:
                                                    "center",
                                                mt: 3,
                                                color:
                                                    "rgba(255,255,255,0.40)",
                                                fontSize: "12px",
                                            }}
                                        >
                                            Already have an
                                            account?{" "}
                                            <Box
                                                component="span"
                                                sx={{
                                                    color:
                                                        "#ff7135",
                                                    fontWeight: 900,
                                                    cursor:
                                                        "pointer",
                                                    ml: 0.5,
                                                    transition:
                                                        "0.2s",

                                                    "&:hover": {
                                                        color:
                                                            "#ff9a65",
                                                        textShadow:
                                                            "0 0 12px rgba(255,90,40,0.45)",
                                                    },
                                                }}
                                                onClick={() =>
                                                    (window.location =
                                                        "/user/login")
                                                }
                                            >
                                                Login
                                            </Box>
                                        </Typography>
                                    </form>
                                )}
                            </Formik>
                        </Box>

                        {/* =================================================
                            VEX INFO STRIP
                        ================================================== */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(3, 1fr)",
                                borderTop:
                                    "1px solid rgba(255,255,255,0.045)",
                                background:
                                    "rgba(0,0,0,0.22)",
                            }}
                        >
                            <InfoCell
                                title="5165"
                                subtitle="CLASSIC CORE"
                            />

                            <InfoCell
                                title="130"
                                subtitle="MAX LEVEL"
                            />

                            <InfoCell
                                title="PVP"
                                subtitle="CLASSIC BATTLES"
                            />
                        </Box>

                        {/* =================================================
                            BOTTOM BRAND STRIP
                        ================================================== */}
                        <Box
                            sx={{
                                px: 3,
                                py: 2,
                                textAlign: "center",
                                borderTop:
                                    "1px solid rgba(255,255,255,0.035)",
                                background:
                                    "rgba(0,0,0,0.28)",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#333",
                                    fontSize: "9px",
                                    fontWeight: 800,
                                    letterSpacing: "2px",
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                VEX • 5165 VERSION 1.0 • CLASSIC CONQUER
                            </Typography>
                        </Box>
                    </Paper>

                    {/* =================================================
                        BOTTOM PAGE MESSAGE
                    ================================================== */}
                    <Box
                        sx={{
                            textAlign: "center",
                            mt: 3,
                        }}
                    >
                        <Typography
                            sx={{
                                color:
                                    "rgba(255,255,255,0.22)",
                                fontSize: "9px",
                                letterSpacing: "2px",
                                fontWeight: 700,
                            }}
                        >
                            YOUR WARRIOR • YOUR GUILD • YOUR LEGACY
                        </Typography>
                    </Box>
                </Container>
            </Box>
        );
    }
}

/* =========================================================
   FIELD STYLE
========================================================= */

const fieldStyle = {
    "& .MuiOutlinedInput-root": {
        minHeight: 54,

        background:
            "linear-gradient(145deg, rgba(255,255,255,0.035), rgba(255,255,255,0.012))",

        borderRadius: "13px",
        color: "#fff",
        transition: "all 0.2s ease",

        "& fieldset": {
            borderColor:
                "rgba(255,75,30,0.13)",
        },

        "&:hover": {
            background:
                "linear-gradient(145deg, rgba(255,70,25,0.045), rgba(255,255,255,0.018))",

            "& fieldset": {
                borderColor:
                    "rgba(255,80,35,0.38)",
            },
        },

        "&.Mui-focused": {
            background:
                "rgba(255,65,20,0.035)",

            "& fieldset": {
                borderColor: "#ff7135",
                borderWidth: "1px",
                boxShadow:
                    "0 0 0 1px rgba(255,65,20,0.05)",
            },
        },
    },

    "& .MuiInputLabel-root": {
        color:
            "rgba(255,255,255,0.42)",
        fontSize: "13px",

        "&.Mui-focused": {
            color: "#ff7135",
        },
    },

    "& .MuiInputLabel-shrink": {
        background: "#0c0908",
        padding: "0 5px",
    },

    "& input": {
        color: "#fff",
        fontSize: "13px",
    },
};

/* =========================================================
   REQUIREMENT ITEM
========================================================= */

const Requirement = ({ text }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,

            px: 1.2,
            py: 1,

            borderRadius: "8px",

            background:
                "rgba(255,65,20,0.025)",

            border:
                "1px solid rgba(255,75,30,0.07)",
        }}
    >
        <Box
            sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "#ff7135",
                boxShadow:
                    "0 0 8px rgba(255,65,20,0.65)",
                flexShrink: 0,
            }}
        />

        <Typography
            sx={{
                color:
                    "rgba(255,255,255,0.34)",
                fontSize: "8px",
                fontWeight: 800,
                letterSpacing: "0.4px",
            }}
        >
            {text}
        </Typography>
    </Box>
);

/* =========================================================
   INFO CELL
========================================================= */

const InfoCell = ({ title, subtitle }) => (
    <Box
        sx={{
            textAlign: "center",
            py: 2,
            borderRight:
                "1px solid rgba(255,255,255,0.035)",

            "&:last-child": {
                borderRight: "none",
            },
        }}
    >
        <Typography
            sx={{
                color: "#ff7135",
                fontWeight: 900,
                fontSize: "15px",
                lineHeight: 1,
                textShadow:
                    "0 0 12px rgba(255,65,20,0.25)",
            }}
        >
            {title}
        </Typography>

        <Typography
            sx={{
                color:
                    "rgba(255,255,255,0.25)",
                fontSize: "7px",
                fontWeight: 800,
                letterSpacing: "1px",
                mt: 0.7,
            }}
        >
            {subtitle}
        </Typography>
    </Box>
);

/* =========================================================
   ERROR STYLE
========================================================= */

const ErrorText = ({ children }) => (
    <Typography
        sx={{
            color: "#ff5c5c",
            fontSize: "11px",
            mt: 0.8,
            ml: 1,
            fontWeight: 600,
        }}
    >
        {children}
    </Typography>
);

export default RegisterPage;

